import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { questionsAPI } from '../lib/api';
import { QuestionCard } from '../components/feed/QuestionCard';
import { SEOHead } from '../components/seo/SEOHead';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  Clock, 
  Users, 
  Search, 
  Plus,
  GitBranch,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const sortOptions = [
  { value: 'recent', label: 'Recent', icon: Clock },
  { value: 'popular', label: 'Popular', icon: TrendingUp },
  { value: 'contributors', label: 'Most Contributors', icon: Users },
];

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');

  useEffect(() => {
    fetchQuestions();
  }, [searchParams]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchParams.get('search') || undefined,
        sort: searchParams.get('sort') || 'recent',
        limit: 20,
      };
      const response = await questionsAPI.getAll(params);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="home-page">
      {/* SEO */}
      <SEOHead
        title={searchParams.get('search') ? `Search: ${searchParams.get('search')}` : null}
        description="Tired of endless threads and buried answers? Clarities transforms crowded discussions into structured, visual mindmaps that everyone can understand and contribute to."
        keywords="mindmap, collaborative learning, knowledge sharing, Q&A, crowd-sourced, structured knowledge"
      />
      
      {/* Hero Section */}
      {!searchParams.get('search') && (
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 text-orange-600 text-sm font-medium rounded-full mb-6">
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                Tired of endless Reddit threads?
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                Turn crowded discussions into{' '}
                <span className="text-orange-500">structured mindmaps</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Information gets lost in comment threads. Clarities transforms complex topics into 
                visual, collaborative mindmaps where knowledge is organized, easy to navigate, 
                and constantly growing with community contributions.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                {isAuthenticated ? (
                  <Button 
                    onClick={() => navigate('/new')}
                    className="btn-accent flex items-center gap-2"
                    data-testid="hero-create-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Ask a Question
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/register')}
                    className="btn-accent flex items-center gap-2"
                    data-testid="hero-signup-btn"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('questions-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="hero-explore-btn"
                >
                  Explore Mindmaps
                </Button>
              </div>
              
              {/* Value Props */}
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <GitBranch className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Visual & Structured</h3>
                    <p className="text-xs text-slate-500 mt-0.5">No more buried answers. See the full picture at a glance.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Community-Driven</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Anyone can add nodes. Best ideas rise to the top.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Ever-Growing</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Knowledge compounds over time with each contribution.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section id="questions-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Sort Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="search-bar">
              <Search className="search-icon w-5 h-5" />
              <input
                type="text"
                placeholder="Search mindmaps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="home-search-input"
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            {sortOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={`tab-button flex items-center gap-1.5 ${sortBy === option.value ? 'active' : ''}`}
                  data-testid={`sort-${option.value}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Results Header */}
        {searchParams.get('search') && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-900">
              Results for &ldquo;{searchParams.get('search')}&rdquo;
            </h2>
            <button 
              onClick={() => {
                setSearchQuery('');
                setSearchParams(new URLSearchParams());
              }}
              className="text-sm text-orange-500 hover:text-orange-600 mt-1"
              data-testid="clear-search-btn"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Questions Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" data-testid="loading-spinner"></div>
          </div>
        ) : questions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {questions.map((question, index) => (
              <div 
                key={question.question_id}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <QuestionCard question={question} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <GitBranch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No mindmaps found</h3>
            <p className="text-slate-500 mb-6">
              {searchParams.get('search') 
                ? 'Try a different search term' 
                : 'Be the first to create a mindmap!'}
            </p>
            {isAuthenticated && (
              <Button 
                onClick={() => navigate('/new')}
                className="btn-accent"
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Mindmap
              </Button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
